import React, { useRef } from 'react';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
  Spinner,
  Tooltip,
  Image,
  HStack,
  Text,
  Box,
} from '@chakra-ui/react';
import { CheckCircleIcon, AttachmentIcon } from '@chakra-ui/icons';
import { UseFormClearErrors } from 'react-hook-form';

interface UploadImageProps {
  label: string;
  fieldName: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  controller: any;
  alt?: string;
  clearErrors?: UseFormClearErrors<any>;
}

const UploadImage: React.FC<UploadImageProps> = ({
  label,
  fieldName,
  isRequired = false,
  isInvalid = false,
  errorMessage = '',
  controller,
  alt,
  clearErrors = () => {},
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const urlFieldName = `${fieldName}Url`;
  const isUploading = controller.uploadingFiles[fieldName];
  const hasUploadedImage = controller.getValues(urlFieldName);
  const fileName = controller.getValues(fieldName)?.name;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      controller.setValue(fieldName, file);
      clearErrors(fieldName);
      controller.setUploadingFiles((prev: Record<string, boolean>) => ({
        ...prev,
        [fieldName]: true,
      }));
      controller.executeUploadFile(file, fieldName);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <FormControl isInvalid={isInvalid} mb={4}>
      <FormLabel fontSize={["xs", "sm", "md"]}>
        {label} {isRequired && <span style={{ color: 'red' }}>*</span>}
      </FormLabel>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box display="flex" flexDirection={["column", "row"]} alignItems={["flex-start", "center"]} gap={[2, 4]}>
        <Button
          leftIcon={<AttachmentIcon />}
          bg='white'
          color='black'
          onClick={openFileDialog}
          size={["xs", "sm"]}
          fontSize={["xs", "sm"]}
          px={[2, 3]}
          _hover={{ bgColor: "gray.100" }}
          width={["100%", "auto"]}
        >
          Subir Imagen
        </Button>

        <HStack spacing={2} align="center" flexWrap="wrap">
          {fileName && (
            <Text fontSize={["xs", "sm"]} noOfLines={1} maxW={["120px", "150px", "200px"]}>
              {fileName}
            </Text>
          )}

          {isUploading && (
            <Tooltip label="Subiendo archivo">
              <Spinner size="sm" color="blue.500" />
            </Tooltip>
          )}

          {hasUploadedImage && !isUploading && (
            <Tooltip label="Archivo subido correctamente">
              <CheckCircleIcon color="green.500" />
            </Tooltip>
          )}
        </HStack>
      </Box>

      {hasUploadedImage && (
        <Box mt={2}>
          <Image
            src={hasUploadedImage.url}
            alt={alt || label}
            objectFit="cover"
            borderRadius="md"
            h={['80px', '100px', '120px']} 
            w={['80px', '100px', '120px']} 
            maxH={['80px', '100px', '120px']}
            maxW={['80px', '100px', '120px']}
          />
        </Box>
      )}

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
};

export default UploadImage;
